import { forwardRef } from 'react';
import { NumberInput, type NumberInputProps } from '@vnetwork/ui/primitives';

/** The slice of a change event that form handlers in this app read. */
export interface NumericInputChangeEvent {
  target: { value: string; name?: string };
}

export interface NumericInputProps
  extends Omit<NumberInputProps, 'value' | 'onChange' | 'precision' | 'min' | 'max'> {
  /** Bounds; numeric strings are accepted like the native attributes. */
  min?: number | string;
  max?: number | string;
  /** Current value as a number or a plain numeric string (`''` / null = empty). */
  value?: string | number | null;
  /**
   * Event-shaped on purpose: it is a drop-in for `<Input type="number">`, so
   * existing `(e) => set(Number(e.target.value))` handlers and RHF
   * `field.onChange` keep working. `e.target.value` is an ungrouped numeric
   * string with a `.` decimal (`"1250000.5"`), or `''` when emptied.
   */
  onChange?: (event: NumericInputChangeEvent) => void;
  /**
   * Maximum fraction digits (never padded). Defaults to 6 so prices, rates and
   * percentages keep the decimals the native number input accepted; pass `0`
   * for counts.
   */
  precision?: number;
}

function toNumber(value: string | number | null | undefined): number | null {
  if (value == null || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Number field with thousands grouping in the app locale — the `@vnetwork/ui`
 * `NumberInput` behind the string contract the native number input had.
 * Negative values are allowed unless `min` is 0 or more, as before.
 */
export const NumericInput = forwardRef<HTMLInputElement, NumericInputProps>(
  function NumericInput({ value, onChange, precision = 6, min, max, name, ...rest }, ref) {
    const lower = toNumber(min) ?? undefined;
    return (
      <NumberInput
        ref={ref}
        {...rest}
        name={name}
        min={lower}
        max={toNumber(max) ?? undefined}
        precision={precision}
        allowNegative={lower == null || lower < 0}
        value={toNumber(value)}
        onChange={(n) => onChange?.({ target: { value: n == null ? '' : String(n), name } })}
      />
    );
  },
);
